import React, { useEffect, useRef, useState } from "react";
import {
  useConstituencies,
  useStates,
} from "../../../hooks/useStatesAndConstituencies";
import sportAPI from "../../../api/admin/sportApi";
import gameAPI from "../../../api/admin/gameApi";
import { showError, showSuccess } from "../../../utils/toastUtils";
import SunEditor from "suneditor-react";
import { MultiSelect } from "react-multi-select-component";

function EditDetails({ data, onSuccess }) {
  const [message, setMessage] = useState("");
  const [sportList, setSportList] = useState([]);
  const today = new Date().toISOString().split("T")[0];

  const [traditionalGameList, setTraditionalGameList] = useState([]);
  const [conventionalGameList, setConventionalGameList] = useState([]);

  const editorRef = useRef();

  const handleEditorChange = (content) => {
    setMessage(content);
  };

  const [formData, setFormData] = useState({
    title: "",
    state: "",
    constituency: "",
    startDate: "",
    endDate: "",
    venue: "",
    traditionalGames: [],
    conventionalGames: [],
  });

  const stateList = useStates();
  const constituencyList = useConstituencies(formData.state);

  useEffect(() => {
    if (data) {
      fetchGameDetails(data.id);
    }
  }, [data]);

  useEffect(() => {
    fetchSportData();
  }, []);

  const fetchGameDetails = async (id) => {
    const res = await gameAPI.getDetails({ id });

    if (res.code === 200) {
      const result = res.data;

      setFormData({
        title: result.title || "",
        state: result.stateId?._id || "",
        constituency: result.constituencyId?._id || "",
        startDate: result.startDate || "",
        endDate: result.endDate || "",
        venue: result.venue || "",
        message: result.message || "",
        traditionalGames: result.traditionalGames || [],
        conventionalGames: result.conventionalGames || [],
      });

      setMessage(result.message);
    }
  };

  const fetchSportData = async () => {
    const res = await sportAPI.getAllforGameCreation();

    if (res.code === 200) {
      const traditionalOptions = res?.data?.traditional.map((item) => ({
        label: item.name,
        value: item._id,
      }));

      const conventionalOptions = res?.data?.conventional.map((item) => ({
        label: item.name,
        value: item._id,
      }));

      setTraditionalGameList(traditionalOptions);
      setConventionalGameList(conventionalOptions);
    }
  };

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    let newErrors = {};

    if (!formData.state.trim()) {
      newErrors.state = "Please select state.";
    }

    if (!formData.constituency.trim()) {
      newErrors.constituency = "Please select constituency.";
    }

    if (!formData.title.trim()) {
      newErrors.title = "Please Enter Title.";
    }

    if (!formData.startDate.trim()) {
      newErrors.startDate = "Please Select Start Date";
    }

    if (!formData.endDate.trim()) {
      newErrors.endDate = "Please Select End Date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "startDate" && formData.endDate && value > formData.endDate) {
      setErrors((prev) => ({
        ...prev,
        startDate: "Start date cannot be after End date",
      }));
    } else if (
      name === "endDate" &&
      formData.startDate &&
      value < formData.startDate
    ) {
      setErrors((prev) => ({
        ...prev,
        endDate: "End date cannot be before Start date",
      }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (!validate()) return;

      formData.message = message;
      formData.id = data.id;

      const res = await gameAPI.update(formData);

      if (res.code === 200) {
        showSuccess(res.message);
        document.getElementById("closeOffcanvasBtn").click();
        onSuccess();
      } else {
        showError(res.message);
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      showError(err.message);
    }
  };

  return (
    <>
      <form
        method="post"
        onSubmit={handleSubmit}
        className="vh-100 d-flex justify-content-between flex-column"
      >
        <div className="offcanvas-header">
          <h4 id="offcanvasRightLabel">Add Details</h4>
          <button
            type="button"
            id="closeOffcanvasBtn"
            className="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          />
        </div>
        <div className="offcanvas-body">
          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">
                Game Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="title"
                className={`form-control ${errors.state ? "is-invalid" : ""}`}
                placeholder="Game Title"
                value={formData.title}
                onChange={handleChange}
              />
              {errors.title && (
                <span className="invalid-feedback d-block">{errors.title}</span>
              )}
            </div>
          </div>

          <div className="row mb-3">
            {/* State */}
            <div className="col-md-6">
              <label htmlFor="state" className="form-label">
                State <span className="text-danger">*</span>
              </label>
              <select
                name="state"
                className={`form-select ${errors.state ? "is-invalid" : ""}`}
                value={formData.state}
                onChange={handleChange}
              >
                <option value="">Select Your State</option>
                {stateList &&
                  stateList.map((item, key) => (
                    <option key={key} value={item._id}>
                      {item.state}
                    </option>
                  ))}
              </select>
              {errors.state && (
                <div className="invalid-feedback d-block">{errors.state}</div>
              )}
            </div>

            {/* Constituency */}
            <div className="col-md-6">
              <label htmlFor="constituency" className="form-label">
                Constituency <span className="text-danger">*</span>
              </label>
              <select
                name="constituency"
                className={`form-select ${
                  errors.constituency ? "is-invalid" : ""
                }`}
                value={formData.constituency}
                onChange={handleChange}
              >
                <option value="">Select Your Constituency</option>
                {constituencyList &&
                  constituencyList.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.constituency}
                    </option>
                  ))}
              </select>
              {errors.constituency && (
                <div className="invalid-feedback d-block">
                  {errors.constituency}
                </div>
              )}
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">Select Game Level</label>
              <div className="row">
                <div className="col-md-6">
                  <input type="radio" className="me-2" />Village Level
                </div>
                <div className="col-md-6">
                  <input type="radio" className="me-2" />Ward Level
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">
                Start Date <span className="text-danger">*</span>
              </label>
              <span className="text-danger error" id="email_error" />
              <input
                type="date"
                name="startDate"
                className={`form-control ${
                  errors.startDate ? "is-invalid" : ""
                }`}
                min={today}
                placeholder="Email Address"
                value={formData.startDate}
                onChange={handleChange}
              />
              {errors.startDate && (
                <span className="invalid-feedback d-block">
                  {errors.startDate}
                </span>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">
                End Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                name="endDate"
                min={formData.startDate || today}
                className={`form-control ${errors.endDate ? "is-invalid" : ""}`}
                value={formData.endDate}
                onChange={handleChange}
              />
              {errors.endDate && (
                <span className="invalid-feedback d-block">
                  {errors.endDate}
                </span>
              )}
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">
                Location <span className="text-danger">*</span>
              </label>
              <span className="text-danger error" id="email_error" />
              <input
                type="text"
                name="venue"
                className={`form-control ${errors.venue ? "is-invalid" : ""}`}
                placeholder="Venue"
                value={formData.venue}
                onChange={handleChange}
              />
              {errors.venue && (
                <span className="invalid-feedback d-block">{errors.venue}</span>
              )}
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">Description (Optional)</label>
              <SunEditor
                getSunEditorInstance={(sunEditor) => {
                  editorRef.current = sunEditor;
                }}
                height="250px"
                setOptions={{
                  buttonList: [
                    ["undo", "redo"],
                    ["font", "fontSize"],
                    ["formatBlock"],
                    ["bold", "underline", "italic", "strike"],
                    ["fontColor", "hiliteColor"],
                    ["align", "list", "table"],
                    ["link", "image", "video"],
                    ["fullScreen", "preview", "print"],
                  ],
                  imageFileInput: true,
                  font: [
                    "Arial",
                    "Comic Sans MS",
                    "Courier New",
                    "Georgia",
                    "Tahoma",
                    "Trebuchet MS",
                    "Verdana",
                  ],
                  fontSize: [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36], // include 12px
                }}
                setContents={message}
                onChange={handleEditorChange}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">
                Traditional Sport <span className="text-danger">*</span>
              </label>
              <span className="text-danger error" id="email_error" />

              <MultiSelect
                options={traditionalGameList}
                value={traditionalGameList.filter((opt) =>
                  formData.traditionalGames.includes(opt.value)
                )}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    traditionalGames: selected.map((s) => s.value),
                  }))
                }
                labelledBy="Select Traditional"
              />
              {errors.venue && (
                <span className="invalid-feedback d-block">{errors.venue}</span>
              )}
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">
                Conventional Sport <span className="text-danger">*</span>
              </label>
              <span className="text-danger error" id="email_error" />
              <MultiSelect
                options={conventionalGameList}
                value={conventionalGameList.filter((opt) =>
                  formData.conventionalGames.includes(opt.value)
                )}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    conventionalGames: selected.map((s) => s.value),
                  }))
                }
                labelledBy="Select Conventional"
              />
              {errors.venue && (
                <span className="invalid-feedback d-block">{errors.venue}</span>
              )}
            </div>
          </div>

          <div className="mt-3 mb-3">
            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={loading}
            >
              {loading ? "Storing Data..." : "Submit"}
            </button>
          </div>
        </div>
      </form>
    </>
  );
}

export default EditDetails;
